import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { getOrders } from '@/api/orders';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, getAuthTokenForRequest, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopOffline } from '@/api/desktopSync';
import { API } from '@/config/constants';
import { resolveBackendUrl } from '@/config/desktopBootstrap';
import { createIncomingDecisionOwner } from './incomingDecisionOwner';

export const INCOMING_REFRESH_INTERVAL_MS = 60_000;

const IncomingOrdersContext = createContext(null);

export function IncomingOrdersProvider({ enabled, sessionKey, children }) {
  const { sessionBinding } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const connection = JSON.stringify([window.lvbDesktop?.backendUrl, window.lvbDesktop?.localBackendUrl]);
  const principal = JSON.stringify([sessionBinding?.user?.id, sessionBinding?.user?.tenant_id,
    sessionBinding?.user?.roles || [sessionBinding?.user?.role], sessionBinding?.source]);
  const ownerKey = JSON.stringify([enabled, sessionKey, revision, principal, connection]);
  const renderedKey = useRef(ownerKey); renderedKey.current = ownerKey;
  const mounted = useRef(false);
  const captureRef = useRef(null);
  const decisions = useMemo(() => createIncomingDecisionOwner(() => captureRef.current?.()), []);
  const decisionStatus = useSyncExternalStore(decisions.subscribe, decisions.getSnapshot);
  const [orders, setOrders] = useState([]);
  const ordersRef = useRef(orders); ordersRef.current = orders;
  const [status, setStatus] = useState(enabled ? 'loading' : 'idle');
  const lifecycleRef = useRef(0);
  const mutationGenerationRef = useRef(0);
  const hasLoadedRef = useRef(false);
  const activeRequestRef = useRef(null);
  const trailingRefreshRef = useRef(false);
  const eligibilityRef = useRef({ enabled, sessionKey, lifecycle: 0 });
  const refreshRef = useRef(null);

  captureRef.current = () => {
    const lifecycle = eligibilityRef.current.lifecycle;
    const current = () => {
      const backend = resolveBackendUrl(window.location.protocol, window.lvbDesktop, process.env.REACT_APP_BACKEND_URL);
      return mounted.current && enabled && renderedKey.current === ownerKey
        && eligibilityRef.current.enabled && eligibilityRef.current.lifecycle === lifecycle
        && eligibilityRef.current.sessionKey === sessionKey
        && sessionBinding?.revision === revision && revision === getActiveSessionRevision()
        && sessionBinding?.source === 'cloud' && !!sessionBinding?.user?.id
        && principal === JSON.stringify([sessionBinding.user.id, sessionBinding.user.tenant_id,
          sessionBinding.user.roles || [sessionBinding.user.role], sessionBinding.source])
        && (sessionBinding.user.roles || [sessionBinding.user.role]).includes('admin')
        && !isDesktopOffline() && backend !== null && API !== null && `${backend || ''}/api` === API
        && connection === JSON.stringify([window.lvbDesktop?.backendUrl, window.lvbDesktop?.localBackendUrl]);
    };
    if (!current()) return null;
    const token = getAuthTokenForRequest(`${API}/orders`);
    if (!token) return null;
    const owns = () => current() && token === getAuthTokenForRequest(`${API}/orders`);
    return {
      key: ownerKey, current: owns,
      resource: { endpoint: API, tenant: sessionBinding.user.tenant_id || null },
      config: { headers: { Authorization: `Bearer ${token}` }, timeout: 30000, suppressServerErrorNotification: true },
      contains: id => owns() && ordersRef.current.some(order => order.id === id && (!order.status || order.status === 'new')),
      invalidate() { if (owns()) mutationGenerationRef.current++; },
      remove(id) {
        if (!owns()) return;
        mutationGenerationRef.current++;
        ordersRef.current = ordersRef.current.filter(order => order.id !== id);
        setOrders(ordersRef.current);
      },
      reconcile() {
        if (!owns()) return Promise.resolve(false);
        if (activeRequestRef.current) {
          trailingRefreshRef.current = true;
          return activeRequestRef.current.promise;
        }
        return refreshRef.current();
      },
      async review() {
        if (activeRequestRef.current) await activeRequestRef.current.promise;
        return owns() ? refreshRef.current() : false;
      },
    };
  };

  const refresh = useCallback(() => {
    const eligibility = eligibilityRef.current;
    const owner = captureRef.current();
    if (!eligibility.enabled || !owner) {
      if (mounted.current && eligibility.enabled) setStatus('unavailable');
      return Promise.resolve(false);
    }

    const active = activeRequestRef.current;
    if (active) {
      if (active.lifecycle !== eligibility.lifecycle) {
        trailingRefreshRef.current = true;
      }
      return active.promise;
    }

    const lifecycle = eligibility.lifecycle;
    const requestMutationGeneration = mutationGenerationRef.current;
    setStatus(hasLoadedRef.current ? 'refreshing' : 'loading');

    const request = Promise.resolve()
      .then(() => owner.current() ? getOrders({ status: 'new' }, owner.config) : null)
      .then((incomingOrders) => {
        const current = eligibilityRef.current;
        if (
          owner.current() && current.enabled
          && current.lifecycle === lifecycle
          && mutationGenerationRef.current === requestMutationGeneration
        ) {
          if (!Array.isArray(incomingOrders) || incomingOrders.some(order => !order || typeof order.id !== 'string' || !order.id)
            || new Set(incomingOrders.map(order => order.id)).size !== incomingOrders.length) throw new Error('Invalid incoming queue');
          ordersRef.current = incomingOrders;
          setOrders(incomingOrders);
          hasLoadedRef.current = true;
          setStatus('ready');
          return true;
        }
        return false;
      })
      .catch(() => {
        const current = eligibilityRef.current;
        if (
          owner.current() && current.enabled
          && current.lifecycle === lifecycle
          && mutationGenerationRef.current === requestMutationGeneration
        ) {
          setStatus(hasLoadedRef.current ? 'ready' : 'unavailable');
        }
        return false;
      })
      .finally(() => {
        if (activeRequestRef.current?.promise === request) {
          activeRequestRef.current = null;
        }
        const current = eligibilityRef.current;
        if (trailingRefreshRef.current && current.enabled) {
          trailingRefreshRef.current = false;
          Promise.resolve().then(() => refreshRef.current?.());
        }
      });

    activeRequestRef.current = { lifecycle, promise: request };
    return request;
  }, []);

  refreshRef.current = refresh;

  useLayoutEffect(() => {
    mounted.current = true;
    const lifecycle = lifecycleRef.current + 1;
    lifecycleRef.current = lifecycle;
    eligibilityRef.current = { enabled, sessionKey, lifecycle };
    mutationGenerationRef.current = 0;
    trailingRefreshRef.current = Boolean(activeRequestRef.current);
    hasLoadedRef.current = false;
    ordersRef.current = [];
    setOrders([]);
    decisions.activate();

    if (!enabled) {
      setStatus('idle');
      return () => {
        mounted.current = false;
        decisions.retire();
        lifecycleRef.current += 1;
        eligibilityRef.current = { enabled: false, sessionKey, lifecycle: lifecycleRef.current };
        trailingRefreshRef.current = false;
      };
    }

    setStatus('loading');
    refreshRef.current();
    const interval = setInterval(
      () => refreshRef.current(),
      INCOMING_REFRESH_INTERVAL_MS,
    );

    return () => {
      mounted.current = false;
      decisions.retire();
      clearInterval(interval);
      lifecycleRef.current += 1;
      eligibilityRef.current = { enabled: false, sessionKey, lifecycle: lifecycleRef.current };
      trailingRefreshRef.current = false;
    };
  }, [decisions, enabled, sessionKey, ownerKey]);

  const value = useMemo(() => ({
    orders,
    status,
    refresh,
    decisions,
    decisionStatus,
    ownerKey,
  }), [orders, refresh, status, decisions, decisionStatus, ownerKey]);

  return (
    <IncomingOrdersContext.Provider value={value}>
      {children}
    </IncomingOrdersContext.Provider>
  );
}

export function useIncomingOrders() {
  const value = useContext(IncomingOrdersContext);
  if (!value) {
    throw new Error('useIncomingOrders requires IncomingOrdersProvider');
  }
  return value;
}
